'use client';

import { c as _c } from "react/compiler-runtime";
import './panel-confirmation.scss';
import { useTranslation } from 'react-i18next';
import { LinkStyle } from '@sola/ark-core/atoms/link-style/link-style.tsx';
import { ArkIcon } from '@sola/ark-icons/lib/ark-icon.tsx';
import { toShortTimeString } from '@sola/utils/date.ts';
import { type VolunteerSlot, formatSlotDateKey, groupSlotsByDate } from '../../../utils/volunteer-utils.ts';
import { VolunteerErrors } from '../errors/volunteer-errors.tsx';
import type { useVolunteerRegistrationSheet } from '../volunteer-registration-sheet.logic.ts';
type Logic = ReturnType<typeof useVolunteerRegistrationSheet>;
const ShiftsByDate: React.FC<{
  contactSlots: VolunteerSlot[];
}> = t0 => {
  const $ = _c(10);
  const {
    contactSlots
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = ["volunteers"];
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const {
    t
  } = useTranslation(t1);
  let t2;
  let t3;
  if ($[1] !== contactSlots || $[2] !== t) {
    const sortedEntries = groupSlotsByDate(contactSlots);
    t2 = "PanelConfirmation__shift-list flex flex-col pl-0";
    let t4;
    if ($[5] !== t) {
      t4 = t5 => {
        const [dateKey, slots] = t5;
        const dateLabel = formatSlotDateKey(dateKey, "short");
        return <li key={dateKey} className="flex flex-col"><span className="pt-0 pb-comfy-sm place-self-stretch label-md label-mild label-neutral-moderate">{dateLabel}</span><ul className="flex flex-col p-0">{slots.map(slot => {
              const role = slot.description ?? t("volunteers:registration.shift");
              const start = toShortTimeString(slot.startDate);
              const end = toShortTimeString(slot.endDate);
              const time = start && end ? `${start} – ${end}` : "";
              return <li key={slot.id} className="flex items-center gap-snug-lg py-snug-xl px-comfy-sm"><span className="PanelConfirmation__check flex shrink-0 items-center justify-center" aria-hidden="true"><ArkIcon iconName="check" color="currentColor" size="20" /></span>{time && <span className="label-mild label-md label-neutral-moderate">{time}</span>}<span className="label-potent label-md label-neutral-moderate">{role}</span></li>;
            })}</ul></li>;
      };
      $[5] = t;
      $[6] = t4;
    } else {
      t4 = $[6];
    }
    t3 = sortedEntries.map(t4);
    $[1] = contactSlots;
    $[2] = t;
    $[3] = t2;
    $[4] = t3;
  } else {
    t2 = $[3];
    t3 = $[4];
  }
  let t4;
  if ($[7] !== t2 || $[8] !== t3) {
    t4 = <ul className={t2}>{t3}</ul>;
    $[7] = t2;
    $[8] = t3;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  return t4;
};
export const PanelConfirmation: React.FC<{
  logic: Logic;
}> = t0 => {
  const $ = _c(21);
  const {
    logic
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = ["volunteers"];
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const {
    t
  } = useTranslation(t1);
  let t2;
  if ($[1] !== t) {
    t2 = t("volunteers:registration.confirmTitle");
    $[1] = t;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  if ($[3] !== t2) {
    t3 = <h3 className="heading-xs md:heading-sm mb-snug-lg">{t2}</h3>;
    $[3] = t2;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  let t4;
  if ($[5] !== t) {
    t4 = t("volunteers:registration.confirmDescription");
    $[5] = t;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  let t5;
  if ($[7] !== t4) {
    t5 = <p className="body-sm body-neutral-mild pb-comfy-xs">{t4}</p>;
    $[7] = t4;
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  let t6;
  if ($[9] !== logic.formErrors) {
    t6 = <VolunteerErrors errors={logic.formErrors} />;
    $[9] = logic.formErrors;
    $[10] = t6;
  } else {
    t6 = $[10];
  }
  let t7;
  if ($[11] !== logic || $[12] !== t) {
    t7 = logic.registrants.map(contact => {
      const fullName = [contact.firstName, contact.lastName].filter(Boolean).join(" ");
      const contactShifts = logic.confirmationShiftsByContact?.[contact.id] ?? [];
      return <li key={contact.id} className="PanelConfirmation__card w-full rounded-md overflow-hidden mx-auto"><div className="PanelConfirmation__personal p-comfy-sm"><div className="mb-comfy-sm"><span className="label-potent label-lg label-neutral-moderate block mb-snug-sm">{t("volunteers:registration.nameLabel")}</span><span className="label-mild label-md label-neutral-moderate block">{fullName}</span></div>{contact.email && <div className="mb-comfy-sm"><span className="label-potent label-lg label-neutral-moderate block mb-snug-sm">{t("volunteers:registration.emailLabel")}</span><span className="label-mild label-md label-neutral-moderate block">{contact.email}</span></div>}{logic.supporterAddress && <div className="mb-comfy-sm"><span className="label-potent label-lg label-neutral-moderate block mb-snug-sm">{t("volunteers:registration.mailingAddressLabel")}</span><div className="label-mild label-md label-neutral-moderate block">{[logic.supporterAddress.street, logic.supporterAddress.street2].filter(Boolean).map(_temp)}<span className="block">{logic.supporterAddress.city},{" "}{logic.supporterAddress.state}{" "}{logic.supporterAddress.postalCode}</span></div></div>}{contact.phone && <div className="mb-comfy-sm"><span className="label-potent label-lg label-neutral-moderate block mb-snug-sm">{t("volunteers:registration.phoneLabel")}</span><span className="label-mild label-md label-neutral-moderate block">{contact.phone}</span></div>}</div><div className="p-comfy-sm"><div className="flex items-center justify-between pb-0"><span className="label-lg label-potent label-neutral-moderate label-sm">{t("volunteers:registration.selectedShifts")}</span><LinkStyle noUnderline={true} size="md" noPadding={true}><button type="button" onClick={() => logic.setStep(1)}>{t("volunteers:registration.editShifts")}</button></LinkStyle></div>{contactShifts.length > 0 ? <ShiftsByDate contactSlots={contactShifts} /> : <span className="body-sm text-neutral-500">{t("volunteers:registration.noShiftsSelected")}</span>}</div></li>;
    });
    $[11] = logic;
    $[12] = t;
    $[13] = t7;
  } else {
    t7 = $[13];
  }
  let t8;
  if ($[14] !== t7) {
    t8 = <ul className="flex flex-col gap-comfy-lg p-0">{t7}</ul>;
    $[14] = t7;
    $[15] = t8;
  } else {
    t8 = $[15];
  }
  let t9;
  if ($[16] !== t3 || $[17] !== t5 || $[18] !== t6 || $[19] !== t8) {
    t9 = <div className="flex flex-col max-w-md mx-auto">{t3}{t5}{t6}{t8}</div>;
    $[16] = t3;
    $[17] = t5;
    $[18] = t6;
    $[19] = t8;
    $[20] = t9;
  } else {
    t9 = $[20];
  }
  return t9;
};
function _temp(line) {
  return <span key={line} className="block">{line}</span>;
}